import type { Incident, RegionSnapshot } from '@wildfire/core';

export type SortKey = 'size' | 'date' | 'state';

export interface Filters {
  region: string;
  states: string[];
  minAcres: number;
  onlyNew24h: boolean;
  sort: SortKey;
}

export interface SidebarProps {
  regions: Array<{ key: string; name: string; states: string[] }>;
  filters: Filters;
  onChange: (next: Filters) => void;
  snapshot: RegionSnapshot | null;
  incidents: Incident[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  loading: boolean;
}

function sizeColor(acres: number): string {
  if (acres >= 10_000) return '#d11414';
  if (acres >= 1_000) return '#f25c1f';
  if (acres >= 100) return '#f6a15c';
  return '#f0d053';
}

export function Sidebar(props: SidebarProps) {
  const { regions, filters, onChange, snapshot, incidents, selectedId, onSelect, loading } = props;
  const regionStates =
    regions.find((r) => r.key === filters.region)?.states ?? [];

  const generatedAt = snapshot?.generatedAt ? new Date(snapshot.generatedAt) : null;
  const summary = snapshot?.summary;
  const hotspotCount = snapshot?.hotspots.length ?? 0;
  const alertCount = snapshot?.alerts.length ?? 0;
  // The Southeast under-reports to IRWIN — surface that explicitly when the
  // filtered incident list is empty so the empty state isn't misread as
  // "nothing is happening" while the FIRMS heatmap clearly shows otherwise.
  const isSoutheast = filters.region === 'southeast';

  return (
    <aside className="sidebar">
      <h1>🔥 wildfire.mx</h1>
      <p className="subtitle">
        <strong>Monitoring eXchange</strong> — live incidents, satellite
        hotspots, and fire-weather alerts.
      </p>
      {alertCount > 0 ? (
        <p className="subtitle" style={{ color: '#f0d053' }}>
          ⚠️ {alertCount} active fire-weather alert{alertCount === 1 ? '' : 's'} in this region.
        </p>
      ) : null}

      <div className="filters">
        <div>
          <label htmlFor="region">Region</label>
          <select
            id="region"
            value={filters.region}
            onChange={(e) => onChange({ ...filters, region: e.target.value, states: [] })}
          >
            {regions.map((r) => (
              <option key={r.key} value={r.key}>
                {r.name}
              </option>
            ))}
          </select>
        </div>

        {regionStates.length > 0 ? (
          <div>
            <label htmlFor="states">States (optional filter)</label>
            <select
              id="states"
              multiple
              value={filters.states}
              onChange={(e) =>
                onChange({
                  ...filters,
                  states: Array.from(e.target.selectedOptions).map((o) => o.value),
                })
              }
            >
              {regionStates.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div>
          <label htmlFor="minAcres">Min acres: {filters.minAcres}</label>
          <input
            id="minAcres"
            type="range"
            min={0}
            max={10000}
            step={10}
            value={filters.minAcres}
            onChange={(e) => onChange({ ...filters, minAcres: Number(e.target.value) })}
            style={{ width: '100%' }}
          />
        </div>

        <div className="row">
          <input
            id="only24h"
            type="checkbox"
            checked={filters.onlyNew24h}
            onChange={(e) => onChange({ ...filters, onlyNew24h: e.target.checked })}
          />
          <label htmlFor="only24h" style={{ margin: 0 }}>
            Only new (last 24h)
          </label>
        </div>

        <div>
          <label htmlFor="sort">Sort by</label>
          <select
            id="sort"
            value={filters.sort}
            onChange={(e) => onChange({ ...filters, sort: e.target.value as SortKey })}
          >
            <option value="size">Size (largest first)</option>
            <option value="date">Discovered (newest first)</option>
            <option value="state">State (A–Z)</option>
          </select>
        </div>
      </div>

      {summary ? (
        <div className="summary">
          <div className="cell">
            <div className="label">Active (NIFC)</div>
            <div className="value">{summary.activeIncidents.toLocaleString()}</div>
          </div>
          <div className="cell">
            <div className="label">Total acres</div>
            <div className="value">{Math.round(summary.totalAcres).toLocaleString()}</div>
          </div>
          <div className="cell">
            <div className="label">New 24h</div>
            <div className="value">{summary.newInLast24h}</div>
          </div>
          <div className="cell">
            <div className="label">Hotspots (48h)</div>
            <div className="value">{hotspotCount.toLocaleString()}</div>
          </div>
        </div>
      ) : null}

      <div className="incident-list">
        {loading ? <div className="subtitle">Loading…</div> : null}
        {incidents.map((i) => (
          <button
            key={i.id}
            type="button"
            className="incident-row"
            style={{
              outline: selectedId === i.id ? '1px solid #f25c1f' : 'none',
              textAlign: 'left',
            }}
            onClick={() => onSelect(i.id === selectedId ? null : i.id)}
          >
            <span className="swatch" style={{ background: sizeColor(i.size.acres) }} />
            <span>
              <div className="name">{i.name}</div>
              <div className="sub">
                {i.location.state} · {i.status}
                {i.size.percentContained != null ? ` · ${i.size.percentContained}%` : ''}
              </div>
            </span>
            <span className="acres">{Math.round(i.size.acres).toLocaleString()} ac</span>
          </button>
        ))}
        {!loading && incidents.length === 0 ? (
          <div className="subtitle" style={{ marginTop: 4 }}>
            {hotspotCount > 0 ? (
              <>
                No NIFC incidents match the current filters, but{' '}
                <strong>{hotspotCount.toLocaleString()} satellite hotspots</strong>{' '}
                were detected in this region in the last 48 hours — see the
                heatmap on the map.
                {isSoutheast
                  ? ' Southeast wildfires often go under-reported in IRWIN; FIRMS may be the only signal.'
                  : ''}
              </>
            ) : (
              <>No incidents match the current filters.</>
            )}
          </div>
        ) : null}
      </div>

      <footer className="credits">
        Data:{' '}
        <a href="https://www.nifc.gov/" target="_blank" rel="noreferrer">
          NIFC / WFIGS
        </a>
        ,{' '}
        <a href="https://firms.modaps.eosdis.nasa.gov/" target="_blank" rel="noreferrer">
          NASA FIRMS
        </a>
        ,{' '}
        <a href="https://www.weather.gov/" target="_blank" rel="noreferrer">
          NWS
        </a>
        .
        {generatedAt ? ` · Updated ${generatedAt.toLocaleTimeString()}` : ''}
        <div style={{ marginTop: 6 }}>William Spurlin — Spurlin LLC</div>
      </footer>
    </aside>
  );
}
